/**
 * ToolRowReveal
 *
 * Wraps a tool row so it gently lists in (fade + small rise) with a per-row
 * stagger, making a step's tools cascade in beneath the reasoning instead of
 * popping. The animation runs once on mount (rows are keyed by tool name +
 * position, so React reuses the node across polls) and is disabled under
 * prefers-reduced-motion via the `.animate-tool-in` keyframe.
 *
 * The stagger uses pre-defined Tailwind arbitrary-value delay classes chosen by
 * index (no inline style), so it stays compliant with the design-system rules
 * while still cascading.
 */
import type { ReactNode } from 'react';

// Static, hoisted delay classes (50ms steps). Index into this by row position;
// rows past the end reuse the last delay so long tool lists don't lag.
const STAGGER_DELAY_CLASSES = [
  '[animation-delay:0ms]',
  '[animation-delay:50ms]',
  '[animation-delay:100ms]',
  '[animation-delay:150ms]',
  '[animation-delay:200ms]',
  '[animation-delay:250ms]',
  '[animation-delay:300ms]',
  '[animation-delay:350ms]',
  '[animation-delay:400ms]',
] as const;

interface ToolRowRevealProps {
  /** Position of the row within the step's tool list (drives the stagger). */
  index: number;
  /** When false, render children plain (e.g. the non-animated sidebar view). */
  animate: boolean;
  children: ReactNode;
}

export function ToolRowReveal({
  index,
  animate,
  children,
}: ToolRowRevealProps) {
  if (!animate) {
    // No wrapper element when not animating — keeps the DOM identical to before.
    return children;
  }
  const delayClass =
    STAGGER_DELAY_CLASSES[Math.min(index, STAGGER_DELAY_CLASSES.length - 1)];
  return <div className={`animate-tool-in ${delayClass}`}>{children}</div>;
}

export default ToolRowReveal;
